import React, { useState, useRef } from 'react';
import { SearchOutlined, CalendarOutlined } from '@ant-design/icons';
import { Table, Button, Card, Row, Input, Col, DatePicker, Select } from 'antd';
import { connect } from 'react-redux';
import Highlighter from 'react-highlight-words';
import { ColumnsType } from 'antd/lib/table';
import moment, { Moment } from 'moment';
import { State, States } from 'sigt';
import { getMovements, Movimiento } from '../../services/movements';
import { useWindowDimensions } from '../../utils/hooks';
import '../../assets/css/components/TableOfficial.css';

interface PropsTable {
  auth: States.Auth;
  thm: States.ThemeColors;
}

interface DataArray extends Movimiento {
  tipo?: string;
}

type TipoMovimientoKey =
  | 'Aprobar pago'
  | 'registro de sucursal'
  | 'rim eliminado'
  | 'actualizar fecha inicio declaración sucursal'
  | 'inmueble_registrado'
  | 'inmueble_modificado'
  | 'cambio de estado TRAMITE'
  | 'Descuento de liquidación';

interface TipoMovimiento {
  value: TipoMovimientoKey;
  label: string;
}

const PAGE_SIZE = 10;

const TableMovements: React.FC<PropsTable> = ({ thm, auth }) => {
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const searchInput = useRef<any>(null);
  const [loading, setLoading] = useState(false);
  const { width } = useWindowDimensions();
  const [data, setData] = useState<DataArray[]>([]);
  const [keys, setKeys] = useState<Moment[]>([]);
  const [selectedTypeMovement, setSelectedTypeMovement] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);

  const getColumnSearchProps = (dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder='Buscar'
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type='primary'
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<SearchOutlined />}
          size='small'
          style={{ width: 90, marginRight: 8 }}
        >
          Buscar
        </Button>
        <Button onClick={() => handleReset(clearFilters)} size='small' style={{ width: 90 }}>
          Restablecer
        </Button>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? thm.primaryColor : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownVisibleChange: (visible) => {
      if (visible && searchInput.current) {
        searchInput.current?.select();
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#1e72c675', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text?.toString() || ''}
        />
      ) : (
        text
      ),
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnFilterDate = (dataIndex: string) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
      setKeys(selectedKeys);
      return (
        <Row style={{ width: 250, padding: 8 }} gutter={[8, 8]}>
          <Col span={24}>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              format='DD/MM/YYYY'
              placeholder={['Fecha inicio ', 'Fecha fin']}
              size='small'
              onChange={(values) => setSelectedKeys(values as any)}
              value={selectedKeys as any}
            />
          </Col>
          <Col span={12}>
            <Button
              style={{ width: '100%' }}
              type='primary'
              icon={<SearchOutlined />}
              size='small'
              onClick={() => confirm()}
            >
              Buscar
            </Button>
          </Col>
          <Col span={12}>
            <Button style={{ width: '100%' }} onClick={() => clearFilters()} size='small'>
              Restablecer
            </Button>
          </Col>
        </Row>
      );
    },
    filterIcon: (filtered) => (
      <CalendarOutlined style={{ color: filtered ? thm.primaryColor : undefined }} />
    ),
    onFilter: (value, record) => {
      return (
        moment(record[dataIndex]).utc().format('DD-MM-YYYY') ===
          keys[0]?.utc().format('DD-MM-YYYY') ||
        moment(record[dataIndex])
          .utc()
          .isBetween(keys[0]?.utc(), keys[1]?.utc(), undefined, '[]')
      );
    },
  });

  // Columnas base para todos los tipos de movimiento
  const baseColumns: ColumnsType<any> = [
    {
      title: 'Id Procedimiento',
      dataIndex: 'id_procedimiento',
      key: 'id_procedimiento',
      align: 'center',
      fixed: 'left',
      ...getColumnSearchProps('id_procedimiento'),
      render: (text) => <>{text ?? 'NO APLICA'}</>,
    },
    {
      title: 'Funcionario',
      dataIndex: 'nombre_completo',
      key: 'nombre_completo',
      align: 'center',
      ...getColumnSearchProps('nombre_completo'),
      render: (text) => <>{text ?? 'NO APLICA'}</>,
    },
    {
      title: 'Documento Funcionario',
      key: 'documento_funcionario',
      align: 'center',
      render: (_, record) =>
        record?.nacionalidad && record.cedula
          ? `${record.nacionalidad}-${record.cedula}`
          : 'NO APLICA',
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      align: 'center',
      ...getColumnSearchProps('descripcion'),
      render: (text) => <>{text ?? 'NO APLICA'}</>,
    },
    {
      title: 'Fecha movimiento',
      dataIndex: 'fecha_movimiento',
      align: 'center',
      ...getColumnFilterDate('fecha_movimiento'),
      render: (text) => <>{text ? moment.utc(text).format('DD/MM/YYYY') : 'NO APLICA'}</>,
    },
    {
      title: 'Tipo de Proceso',
      dataIndex: 'tipo',
      key: 'tipo',
      align: 'center',
      ...getColumnSearchProps('tipo'),
      render: (text) => <>{text ?? 'NO APLICA'}</>,
    },
  ];

  // Columnas adicionales solo para 'Aprobar pago'
  const paymentColumns: ColumnsType<any> = [
    {
      title: 'Id Solicitud',
      dataIndex: 'id_solicitud',
      key: 'id_solicitud',
      align: 'center',
      fixed: 'left',
      ...getColumnSearchProps('id_solicitud'),
      render: (text) => <>{text ?? 'NO APLICA'}</>,
    },
    {
      title: 'Funcionario',
      dataIndex: 'nombre_completo',
      key: 'nombre_completo',
      align: 'center',
      ...getColumnSearchProps('nombre_completo'),
      render: (text) => <>{text ?? 'NO APLICA'}</>,
    },
    {
      title: 'Contribuyente',
      dataIndex: 'razon_social',
      key: 'razon_social',
      align: 'center',
      ...getColumnSearchProps('razon_social'),
      render: (text) => <>{text ?? 'NO APLICA'}</>,
    },
    {
      title: 'Documento',
      dataIndex: 'documento',
      key: 'documento',
      align: 'center',
      ...getColumnSearchProps('documento'),
      render: (text) => <>{text ?? 'NO APLICA'}</>,
    },
    {
      title: 'Referencia',
      dataIndex: 'referencia',
      key: 'referencia',
      align: 'center',
      ...getColumnSearchProps('referencia'),
      render: (text) => <>{text ?? 'NO APLICA'}</>,
    },
    {
      title: 'Banco Destino',
      dataIndex: 'banco_destino',
      key: 'banco_destino',
      align: 'center',
      ...getColumnSearchProps('banco_destino'),
      render: (text) => <>{text ?? 'NO APLICA'}</>,
    },
    {
      title: 'Monto',
      dataIndex: 'monto',
      key: 'monto',
      align: 'center',
      render: (text) => <>{text ?? 'NO APLICA'}</>,
    },
    {
      title: 'Fecha de pago',
      dataIndex: 'fecha_de_pago',
      align: 'center',
      ...getColumnFilterDate('fecha_de_pago'),
      render: (text) => <>{text ? moment.utc(text).format('DD/MM/YYYY') : 'NO APLICA'}</>,
    },
    {
      title: 'Fecha movimiento',
      dataIndex: 'fecha_movimiento',
      align: 'center',
      ...getColumnFilterDate('fecha_movimiento'),
      render: (text) => <>{text ? moment.utc(text).format('DD/MM/YYYY') : 'NO APLICA'}</>,
    },
    {
      title: 'Concepto',
      dataIndex: 'concepto_pago',
      key: 'concepto_pago',
      align: 'center',
      ...getColumnSearchProps('concepto_pago'),
      render: (text) => <>{text ?? 'NO APLICA'}</>,
    },
    {
      title: 'Tipo de Proceso',
      dataIndex: 'tipo',
      key: 'tipo',
      align: 'center',
      ...getColumnSearchProps('tipo'),
      render: (text) => <>{text ?? 'NO APLICA'}</>,
    },
  ];

  const getColumnsByType = (type: string): ColumnsType<any> => {
    return type === 'Aprobar pago' ? paymentColumns : baseColumns;
  };

  const tipoMovimiento: TipoMovimiento[] = [
    { value: 'Aprobar pago', label: 'Aprobar pago' },
    { value: 'registro de sucursal', label: 'Registro de sucursal' },
    { value: 'rim eliminado', label: 'RIM eliminado' },
    { value: 'actualizar fecha inicio declaración sucursal', label: 'Actualizar fecha inicio declaración sucursal' },
    { value: 'inmueble_registrado', label: 'Inmueble registrado' },
    { value: 'inmueble_modificado', label: 'Inmueble modificado' },
    { value: 'cambio de estado TRAMITE', label: 'Cambio de estado trámite' },
    { value: 'Descuento de liquidación', label: 'Descuento de liquidación' },
  ];

  const fetchMovements = async (page = 1) => {
    if (auth.token && selectedTypeMovement) {
      try {
        setLoading(true);
        setData([]);

        const response = await getMovements(selectedTypeMovement, page, auth.token);

        const formattedData = response.movimientos.map((ele, index) => ({
          ...ele,
          key: `${page}-${index}`,
          tipo: ele.tipo_movimiento,
        }));

        setData(formattedData);
        setTotalRows(response.totalRows);
        setCurrentPage(page);
      } catch (e) {
        console.error('Error fetching movements:', e);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Card
      style={{ height: '100%' }}
      title='Bitacora'
      headStyle={{
        backgroundColor: thm.primaryColor,
        padding: width < 992 ? '0 10px' : '0 20px',
        color: '#fff',
      }}
      bodyStyle={{ overflowY: 'auto' }}
    >
      <Row gutter={16} justify='start' align='middle'>
        <Col span={10}>
          <Col span={24}>
            <label>Tipo de movimiento</label>
          </Col>
          <Col span={24}>
            <Select
              disabled={loading}
              placeholder='Selecciona un tipo de movimiento'
              style={{ width: '100%' }}
              onChange={(e: any) => {
                setSelectedTypeMovement(e);
                setData([]);
              }}
              options={tipoMovimiento}
            />
          </Col>
        </Col>
        <Col span={6}>
          <Button
            type='primary'
            loading={loading}
            onClick={() => fetchMovements()}
            style={{ marginTop: 22 }}
          >
            Buscar
          </Button>
        </Col>
      </Row>

      <Row gutter={16} justify='start' align='middle'>
        <Col span={24} style={{ marginBlock: 20 }}>
          <Table
            className='tableScroll'
            scroll={{ x: 'max-content' }}
            style={{ height: '100%' }}
            size='middle'
            loading={loading}
            columns={getColumnsByType(selectedTypeMovement)}
            dataSource={data}
            pagination={{
              current: currentPage,
              total: totalRows,
              onChange: (page) => fetchMovements(page),
              pageSize: PAGE_SIZE,
              size: 'small',
            }}
          />
        </Col>
      </Row>
    </Card>
  );
};

const mapStateToProps = ({ auth, thm }: State) => ({ auth, thm });

export default connect(mapStateToProps, {})(TableMovements);
